import { setMetaWhen, yup } from '@framework/validation';
import { WidgetDirectivesRepository } from '../../schema/directives/WidgetDirectivesRepository';
import { IRule } from '../../schema/rules/IRule';
import { extractRule } from '../../schema/rules/repository/extractRule';
import {
  IParentWidgetScope,
  IWidgetType,
} from '../../schema/widgets/IWidgetType';
import { Widget } from '../../schema/widgets/Widget';
import { setConfirmationFormSchemaRules } from '../../schema/widgets/repository/advanced/confirm/shared/setConfirmationFormSchemaRules';
import { isInputWidget } from '../../schema/widgets/repository/inputs/isInputWidget';
import { setEnabledCondition } from '../setEnabledCondition';
import { setVisibleCondition } from '../setVisibleCondition';
import { setWidgetDefaultValue } from '../utils/setWidgetDefaultValue';
import { setWidgetSchemaLabel } from '../utils/setWidgetSchemaLabel';
import { FormBuilderMetaFlags } from './flags/FormBuilderMetaFlags';

/**
 * Imposta tutta la validazione seguendo la configurazione delle regole di validazione,
 * di visibilità, le label etc.
 */
export function setSchemaRules<W extends Widget>(
  schema: yup.AnySchema,
  options: {
    widget: W;
    widgetType: IWidgetType<W> | null;
    parentScope: IParentWidgetScope;
  },
) {
  const { widget, widgetType, parentScope } = options;

  // NB non possiamo fare check su schema.spec.optional per gestire il required
  // perché qui non è ancora calcolato.
  // schema = schema.notRequired();

  if (!widgetType) return schema;

  const flags = schema.meta?.() ?? {};

  // setEditableRule deve essere chiamato prima di setValidationRules
  // per poter leggere il valore meta 'enabled' dello schema
  schema = setEditableRule(schema, widget, widgetType, parentScope);

  // Le regole di validazione (required, range, format, ...) vengono
  // applicate _solo_ se il meta "applyRules" è true (o undefined).
  if (!flags[FormBuilderMetaFlags.SkipValidationRules]) {
    schema = setValidationRules(schema, widget, widgetType);
  }

  // Se configurato, applica le regole di validazione post-rules
  schema = setPostApplyRulesWhen(schema);

  // Aggiungiamo tutte le regole di validazione definite nelle direttive
  const directives = WidgetDirectivesRepository.getConfiguredInWidget(widget);

  for (const directive of directives) {
    if (!directive.directive.formSchemaModifier) continue;

    schema = directive.directive.formSchemaModifier({
      ...options,
      widgetType,
      schema,
      flags,
      directiveConfig: directive.directiveConfig,
    });
  }

  schema = setVisibilityRule(schema, widget, widgetType, parentScope); // prettier-ignore

  if (!flags[FormBuilderMetaFlags.SkipDefaultValue]) {
    schema = defaultValue(schema, widget, widgetType);
  }

  schema = setConfirmationFormSchemaRules(schema, flags, options);
  if (!flags[FormBuilderMetaFlags.SkipLabel]) {
    schema = setLabel(schema, widget, widgetType);
  }

  return schema;
}

/**
 * Imposta il validatore in base alle regole di validazione configurate.
 * Le regole vengono applicate solo se il flag nei meta "applyRules" è true (o undefined).
 * questo permette di gestire la validazione parziale.
 * Non è possibile mettere la setValidationRules direttamente nel when della
 * validazione parziale, perché yup non ordina i campi in base alle dipendenze
 * dei when annidati (le dipendenze sono calcolate a build-time e non a run-time).
 */
export function setValidationRules<W extends Widget>(
  schema: yup.AnySchema,
  widget: W,
  widgetType: IWidgetType<W>,
) {
  if (!isInputWidget(widget)) return schema;

  // 1. Regole dinamiche, così come configurate concretamente nel widget
  const rules = widgetType.rules ?? [];
  rules.forEach(rule => {
    const ruleConfig = extractRule<IRule>(widget.rules, rule.type);
    if (ruleConfig)
      schema = schema.when([], (_, schema) => {
        const applyRules = schema.meta?.()?.applyRules ?? true;
        // Controllare se il widget non è enabled non applicare le regole di validazione
        const isEnabled = schema.meta?.()?.enabled ?? true;
        if (!applyRules || !isEnabled) return schema;

        const resultSchema = rule.validator(schema, ruleConfig);
        return resultSchema;
      });
  });

  // 2. Regole statiche, sono inserite nel `widgetType` solo
  //    per evitare che siano applicate in fase di compilazione
  //    se si è in una validazione parziale, in un'altra sezione, ecc.
  widgetType.fixedRules?.forEach(rule => {
    schema = schema.when([], (_, schema) => {
      const applyRules = schema.meta?.()?.applyRules ?? true;
      // Controllare se il widget non è enabled non applicare le regole di validazione
      const isEnabled = schema.meta?.()?.enabled ?? true;
      if (!applyRules || !isEnabled) return schema;

      const resultSchema = rule.validator(schema, widget);
      return resultSchema;
    });
  });

  return schema;
}

/**
 * Imposta la visibilità del widget.
 */
export function setVisibilityRule<W extends Widget>(
  schema: yup.AnySchema,
  widget: W,
  widgetType: IWidgetType<W>,
  parentScope: IParentWidgetScope,
) {
  return setVisibleCondition(schema, widget, widgetType, parentScope);
}

/**
 * Imposta l'attivazione/disattivazione del widget.
 */
export function setEditableRule<W extends Widget>(
  schema: yup.AnySchema,
  widget: W,
  widgetType: IWidgetType<W>,
  parentScope: IParentWidgetScope,
) {
  if (widgetType.alwaysEnabled) return schema.enabled(true);
  return setEnabledCondition(schema, widget, widgetType, parentScope);
}

/**
 * Imposta la label del widget.
 */
export function setLabel<W extends Widget>(
  schema: yup.AnySchema,
  widget: W,
  widgetType: IWidgetType<W>,
) {
  return setWidgetSchemaLabel(widget, schema);
}

export function defaultValue<W extends Widget>(
  schema: yup.AnySchema,
  widget: W,
  widgetType: IWidgetType<W>,
) {
  return setWidgetDefaultValue(widget, schema);
}

/**
 * Tag dei meta-when utili per impostare i when in varie fasi della costruizione dello schema.
 * In questo modo è possibile definire un when che verrà applicato _dopo_ altre operazioni (e.g. set di meta/regole)
 */
export enum SchemaBuilderWhenTags {
  PostApplyRules = 'postApplyRules',
}

/**
 * Se sono stati configurati dei metaWhen, li applica.
 * In questo modo, è possibile configurare un when nel validatore
 * che verrà però applicato _dopo_ i meta `enable` e `applyRules`.
 *
 * Per esempio, per i campi dei PaymentRow, è possibile configurare il required
 * opzionale soltanto se `applyRules` è true, ma `applyRules` è impostato
 * _dopo_ la creazione dello schema stesso, quindi deve essere configurato
 * come extra when.
 */
export function setPostApplyRulesWhen(schema: yup.AnySchema) {
  return setMetaWhen(SchemaBuilderWhenTags.PostApplyRules, schema);
}
